import type { Booking } from '@/services/bookings';
import type { Checklist } from '@/services/checklists';
import type { ItineraryItem } from '@/services/itinerary';
import type { Trip } from '@/services/trips';
import type { TripBudgetSnapshot } from '@/types/budget';
import type { DayWeather } from '@/components/maps/DaySummaryCard';
import type { TripAlert } from '@/utils/tripAlerts';
import { formatCurrency } from '@/utils/currency';
import { getTripDayContext, type TripDayContext } from '@/utils/tripDayContext';
import { getItemTripDayNumber } from '@/utils/tripCalendar';
import { resolveItemStartInstant, toDateKeyInTimezone } from '@/utils/tripItemSchedule';

export type DayBriefingPriority = 'high' | 'medium' | 'low';
export type DayBriefingItemKind = 'weather' | 'booking' | 'checklist' | 'budget' | 'safety' | 'itinerary';

export type DayBriefingItem = {
  id: string;
  kind: DayBriefingItemKind;
  title: string;
  detail?: string;
  actionLabel?: string;
  priority: DayBriefingPriority;
  routeTarget?: 'plan' | 'documents' | 'wallet' | 'safety' | 'share' | 'collaborators';
};

export type DayBriefing = {
  tripId: string;
  dayContext: TripDayContext;
  todayItems: ItineraryItem[];
  nextItem: ItineraryItem | null;
  weather: DayWeather | null;
  items: DayBriefingItem[];
  trayLine: string;
};

type BuildDayBriefingOptions = {
  now?: Date;
  itineraryItems?: ItineraryItem[];
  bookings?: Booking[];
  checklist?: Checklist | null;
  budgetSnapshot?: TripBudgetSnapshot;
  alerts?: TripAlert[];
  weather?: DayWeather | null;
};

const getStartMs = (item: ItineraryItem, timeZone: string, trip: Trip): number =>
  resolveItemStartInstant(item, timeZone, trip).getTime();

const formatTime = (value?: string): string | null => {
  if (!value) return null;
  const [hoursRaw, minutes = '00'] = value.split(':');
  const hours = Number(hoursRaw);
  if (!Number.isFinite(hours)) return null;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes} ${suffix}`;
};

const weatherLabel = (weather: DayWeather): string => {
  const condition = weather.condition.replace(/_/g, ' ');
  if (typeof weather.tempCurrent === 'number') {
    return `${Math.round(weather.tempCurrent)}° · ${condition}`;
  }
  if (weather.tempHigh === weather.tempLow) {
    return `${Math.round(weather.tempHigh)}° · ${condition}`;
  }
  return `${Math.round(weather.tempLow)}°–${Math.round(weather.tempHigh)}° · ${condition}`;
};

const bookingDate = (booking: Booking): Date | null => {
  return booking.departureDate ?? booking.checkInDate ?? booking.arrivalDate ?? booking.checkOutDate ?? null;
};

const isSameDay = (left: Date, right: Date, timeZone: string): boolean =>
  toDateKeyInTimezone(left, timeZone) === toDateKeyInTimezone(right, timeZone);

const buildTrayLine = (
  trip: Trip,
  context: TripDayContext,
  nextItem: ItineraryItem | null,
  weather: DayWeather | null,
  now: Date,
): string => {
  const parts: string[] = [];
  if (context.currentDay) {
    parts.push(`Day ${context.currentDay}`);
  } else if (context.daysUntilStart > 0) {
    parts.push(context.daysUntilStart === 1 ? 'Starts tomorrow' : `Starts in ${context.daysUntilStart} days`);
  }
  if (nextItem) {
    const time = getStartMs(nextItem, context.timeZone, trip) - now.getTime();
    const hours = Math.ceil(time / (60 * 60 * 1000));
    parts.push(hours > 0 && hours <= 24 ? `${nextItem.title} in ${hours}h` : nextItem.title);
  }
  if (weather) {
    parts.push(weather.condition === 'rainy' ? 'Rain later' : weather.condition.replace(/_/g, ' '));
  }
  return parts.length ? parts.join(' · ') : trip.name;
};

export const buildDayBriefing = (trip: Trip, options: BuildDayBriefingOptions = {}): DayBriefing => {
  const now = options.now ?? new Date();
  const dayContext = getTripDayContext(trip, now);
  const { timeZone } = dayContext;
  const itineraryItems = options.itineraryItems ?? [];
  const todayItems = itineraryItems
    .filter(
      (item) =>
        dayContext.currentDay != null &&
        getItemTripDayNumber(trip, item) === dayContext.currentDay,
    )
    .sort((a, b) => getStartMs(a, timeZone, trip) - getStartMs(b, timeZone, trip));
  const upcomingToday =
    todayItems.find((item) => getStartMs(item, timeZone, trip) >= now.getTime()) ?? null;
  const nextItem =
    upcomingToday ??
    [...itineraryItems]
      .filter((item) => item.status !== 'completed')
      .sort((a, b) => getStartMs(a, timeZone, trip) - getStartMs(b, timeZone, trip))
      .find((item) => getStartMs(item, timeZone, trip) >= now.getTime()) ??
    null;
  const briefingItems: DayBriefingItem[] = [];

  if (options.weather) {
    briefingItems.push({
      id: `weather-${trip.id}-${dayContext.todayKey}`,
      kind: 'weather',
      title: weatherLabel(options.weather),
      detail: options.weather.condition === 'rainy' ? 'Pack an umbrella or rain jacket.' : undefined,
      priority: ['rainy', 'stormy', 'snowy'].includes(options.weather.condition) ? 'high' : 'low',
    });
  }

  const todayBookings = (options.bookings ?? [])
    .filter((booking) => {
      const date = bookingDate(booking);
      return date ? isSameDay(date, now, timeZone) : false;
    })
    .slice(0, 2);
  todayBookings.forEach((booking) => {
    const time = booking.startTime ?? booking.segments?.[0]?.departureTime;
    briefingItems.push({
      id: `booking-${booking.id}`,
      kind: 'booking',
      title: booking.type === 'flight' ? 'Flight today' : `${booking.type} today`,
      detail: [booking.provider, time ? formatTime(time) : null, booking.confirmationNumber].filter(Boolean).join(' · '),
      actionLabel: booking.type === 'flight' ? 'Review check-in' : 'View booking',
      priority: 'high',
      routeTarget: 'plan',
    });
  });

  const openChecklistItems = options.checklist?.items?.filter((item) => !item.completed) ?? [];
  if (openChecklistItems.length > 0) {
    const urgent = openChecklistItems
      .filter((item) => item.dueDate && item.dueDate.getTime() <= now.getTime() + 24 * 60 * 60 * 1000)
      .slice(0, 2);
    const selected = urgent.length ? urgent : openChecklistItems.slice(0, 2);
    briefingItems.push({
      id: `checklist-${trip.id}`,
      kind: 'checklist',
      title: `${openChecklistItems.length} checklist ${openChecklistItems.length === 1 ? 'item' : 'items'} left`,
      detail: selected.map((item) => item.title).join(', '),
      actionLabel: 'Open checklist',
      priority: urgent.length ? 'high' : 'medium',
      routeTarget: 'plan',
    });
  }

  if (options.budgetSnapshot && typeof trip.budget === 'number' && trip.budget > 0) {
    const spent = (options.budgetSnapshot.expensesConverted ?? 0) + (options.budgetSnapshot.bookingsConverted ?? 0);
    const budgetPercent = Math.round((spent / trip.budget) * 100);
    const tripPercent = dayContext.progressPercent;
    if (budgetPercent >= 90 || budgetPercent > tripPercent + 20) {
      briefingItems.push({
        id: `budget-${trip.id}`,
        kind: 'budget',
        title: `${budgetPercent}% of budget used`,
        detail: `${formatCurrency(spent, options.budgetSnapshot.baseCurrency)} of ${formatCurrency(trip.budget, options.budgetSnapshot.baseCurrency)}`,
        actionLabel: 'Review budget',
        priority: budgetPercent >= 100 ? 'high' : 'medium',
        routeTarget: 'wallet',
      });
    }
  }

  (options.alerts ?? []).slice(0, 2).forEach((alert) => {
    const routeTarget: DayBriefingItem['routeTarget'] =
      alert.type === 'docs'
        ? 'documents'
        : alert.type === 'budget'
          ? 'wallet'
          : alert.type === 'collaborators'
            ? 'collaborators'
            : alert.type === 'visa'
              ? 'safety'
              : 'plan';
    briefingItems.push({
      id: `alert-${alert.id}`,
      kind:
        alert.type === 'budget'
          ? 'budget'
          : alert.type === 'docs'
            ? 'checklist'
            : alert.type === 'collaborators'
              ? 'itinerary'
              : 'safety',
      title: alert.title,
      detail: alert.message,
      actionLabel: alert.actionLabel,
      priority: alert.priority,
      routeTarget,
    });
  });

  if (!briefingItems.some((item) => item.kind === 'itinerary') && todayItems.length > 0) {
    briefingItems.push({
      id: `itinerary-${trip.id}-${dayContext.todayKey}`,
      kind: 'itinerary',
      title: `${todayItems.length} plan ${todayItems.length === 1 ? 'today' : 'items today'}`,
      detail: nextItem ? `Next: ${nextItem.title}` : undefined,
      actionLabel: 'Open plan',
      priority: 'low',
      routeTarget: 'plan',
    });
  }

  const priorityOrder: Record<DayBriefingPriority, number> = { high: 0, medium: 1, low: 2 };
  const items = briefingItems.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]).slice(0, 4);

  return {
    tripId: trip.id,
    dayContext,
    todayItems,
    nextItem,
    weather: options.weather ?? null,
    items,
    trayLine: buildTrayLine(trip, dayContext, nextItem, options.weather ?? null, now),
  };
};
